import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  message,
  Row,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined, CopyOutlined, EditOutlined, LineChartOutlined, ShoppingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import urls from "urls";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import { FIRESTORE_CAMPAIGNS_TABLE } from "constants/FirestoreConstant";
import { formatPrice } from "functions/formatters";
import { CURRENCY } from "constants/DeliveryConstants";
import Loading from "components/shared-components/Loading";
import { useCampaign } from "data/hooks/useCampaigns";
import { useCampaignPerformance } from "data/hooks/useCampaignPerformance";
import { useUTMs } from "data/hooks/useUTMs";
import { useProducts } from "data/hooks/useProducts";
import { useCategories } from "data/hooks/useCategories";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

const { Title, Text, Paragraph } = Typography;

const CampaignDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");

  const searchParams = new URLSearchParams(location.search);
  const shopSource = searchParams.get("shopSource") || "VCE";

  const { campaign, isLoading } = useCampaign(id, shopSource);
  const { utms, isLoading: utmsLoading } = useUTMs({ shopSource });
  const { products, isLoading: productsLoading } = useProducts();
  const { categories, isLoading: categoriesLoading } = useCategories();
  const {
    metrics,
    productAnalysis,
    crossSellAnalysis,
    attributionBreakdown,
    timeSeriesData,
    isLoading: metricsLoading,
  } = useCampaignPerformance(id, shopSource);

  const campaignUtms = campaign?.utmIds?.map((utmId) => utms.find((u) => u.id === utmId)).filter(Boolean) || [];

  const getProductName = (productId) => {
    const product = products.find((p) => p.id === productId);
    return product ? product.name || product.title || productId : productId;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name || category.title || categoryId : categoryId;
  };

  const handleDelete = async () => {
    try {
      const firestore = shopSource === "VCE" ? vceFirestore : hvoFirestore;
      await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).doc(id).delete();
      message.success(t("campaign_deleted"));
      navigate(urls.campaigns);
    } catch (error) {
      console.error("Error deleting campaign:", error);
      message.error(t("error_deleting_campaign"));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      active: "success",
      paused: "warning",
      completed: "error",
    };
    return colors[status] || "default";
  };

  const getPlatformColor = (platform) => {
    const colors = {
      facebook_ads: "blue",
      google_ads: "green",
      instagram_ads: "purple",
      email_marketing: "orange",
      other: "default",
    };
    return colors[platform] || "default";
  };

  const generateUTMUrl = (utm) => {
    if (!utm) return "";

    const baseUrl =
      utm.customUrl || (shopSource === "VCE" ? "https://hetvleescentrum.nl" : "https://halalvlees-online.nl");
    const params = new URLSearchParams();
    if (utm.source) params.append("utm_source", utm.source);
    if (utm.medium) params.append("utm_medium", utm.medium);
    if (utm.campaign) params.append("utm_campaign", utm.campaign);
    if (utm.term) params.append("utm_term", utm.term);
    if (utm.content) params.append("utm_content", utm.content);

    return `${baseUrl}?${params.toString()}`;
  };

  const copyUTMUrl = (utm) => {
    const url = generateUTMUrl(utm);
    navigator.clipboard.writeText(url);
    message.success(t("notifications.utm_url_copied"));
  };

  const formatDate = (date) => {
    if (!date) return "-";
    if (date.toDate && typeof date.toDate === "function") {
      return dayjs(date.toDate()).format("DD-MM-YYYY");
    } else if (date instanceof Date) {
      return dayjs(date).format("DD-MM-YYYY");
    } else {
      return dayjs(date).format("DD-MM-YYYY");
    }
  };

  if (isLoading || !campaign) {
    return <Loading cover="content" />;
  }

  const productColumns = [
    {
      title: t("product"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("quantity_sold"),
      dataIndex: "quantity",
      key: "quantity",
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: t("revenue"),
      dataIndex: "revenue",
      key: "revenue",
      render: (revenue) => CURRENCY + formatPrice(revenue),
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: t("orders"),
      dataIndex: "orders",
      key: "orders",
      sorter: (a, b) => a.orders - b.orders,
    },
  ];

  const crossSellColumns = [
    {
      title: t("product_1"),
      dataIndex: "product1",
      key: "product1",
      render: (product) => product.name,
    },
    {
      title: t("product_2"),
      dataIndex: "product2",
      key: "product2",
      render: (product) => product.name,
    },
    {
      title: t("times_bought_together"),
      dataIndex: "count",
      key: "count",
      sorter: (a, b) => a.count - b.count,
    },
    {
      title: t("combined_revenue"),
      dataIndex: "revenue",
      key: "revenue",
      render: (revenue) => CURRENCY + formatPrice(revenue),
      sorter: (a, b) => a.revenue - b.revenue,
    },
  ];

  const attributionData = Object.entries(attributionBreakdown).map(([method, count]) => ({
    method: t(method),
    count,
  }));

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.campaigns)}>
              {t("back")}
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {campaign.name}
            </Title>
            <Tag color={shopSource === "VCE" ? "cyan" : "magenta"}>{shopSource}</Tag>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`${urls.editCampaign}/${id}?shopSource=${shopSource}`)}
            >
              {t("edit_campaign")}
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={t("campaign_details")}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label={t("campaign_name")}>{campaign.name}</Descriptions.Item>

              <Descriptions.Item label={t("platform")}>
                <Tag color={getPlatformColor(campaign.platform)}>{t(campaign.platform)}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label={t("status")}>
                <Tag color={getStatusColor(campaign.status)}>{t(campaign.status)}</Tag>
              </Descriptions.Item>

              {campaign.type && (
                <Descriptions.Item label={t("campaign_type")}>
                  <Tag>{t(campaign.type)}</Tag>
                </Descriptions.Item>
              )}

              {campaign.goal && (
                <Descriptions.Item label={t("campaign_goal")}>
                  <Tag>{t(campaign.goal)}</Tag>
                </Descriptions.Item>
              )}

              <Descriptions.Item label={t("start_date")}>{formatDate(campaign.startDate)}</Descriptions.Item>

              <Descriptions.Item label={t("end_date")}>
                {campaign.isOngoing ? <Tag color="blue">{t("ongoing")}</Tag> : formatDate(campaign.endDate)}
              </Descriptions.Item>

              <Descriptions.Item label={t("budget")}>
                {campaign.budget ? `€${campaign.budget.toLocaleString()}` : "-"}
              </Descriptions.Item>

              {campaign.description && (
                <Descriptions.Item label={t("campaign_description")}>
                  <Paragraph>{campaign.description}</Paragraph>
                </Descriptions.Item>
              )}

              {campaign.objectives && (
                <Descriptions.Item label={t("objectives")}>
                  <Paragraph>{campaign.objectives}</Paragraph>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {(campaign.discountCodes?.length > 0 ||
            campaign.targetProducts?.length > 0 ||
            campaign.targetCategories?.length > 0 ||
            campaign.channels?.length > 0) && (
            <Card title={t("campaign_attribution")} style={{ marginTop: 16 }}>
              <Descriptions column={1} bordered>
                {campaign.discountCodes?.length > 0 && (
                  <Descriptions.Item label={t("discount_codes")}>
                    {campaign.discountCodes.map((code) => (
                      <Tag key={code} color="green">
                        {code}
                      </Tag>
                    ))}
                  </Descriptions.Item>
                )}

                {campaign.targetProducts?.length > 0 && (
                  <Descriptions.Item label={t("target_products")}>
                    {campaign.targetProducts.map((productId) => (
                      <Tag key={productId}>{getProductName(productId)}</Tag>
                    ))}
                  </Descriptions.Item>
                )}

                {campaign.targetCategories?.length > 0 && (
                  <Descriptions.Item label={t("target_categories")}>
                    {campaign.targetCategories.map((categoryId) => (
                      <Tag key={categoryId}>{getCategoryName(categoryId)}</Tag>
                    ))}
                  </Descriptions.Item>
                )}

                {campaign.channels?.length > 0 && (
                  <Descriptions.Item label={t("channels")}>
                    {campaign.channels.map((channel) => (
                      <Tag key={channel} color="blue">
                        {channel}
                      </Tag>
                    ))}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t("campaign_metrics")} loading={metricsLoading} style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <Statistic title={t("total_orders")} value={metrics.totalOrders} />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <Statistic title={t("total_revenue")} value={metrics.totalRevenue} prefix="€" precision={2} />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <Statistic title={t("new_customers")} value={metrics.newCustomers} />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <Statistic title={t("avg_order_value")} value={metrics.averageOrderValue} prefix="€" precision={2} />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <Statistic title={t("unique_customers")} value={metrics.uniqueCustomers} />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <Statistic title={t("new_customer_rate")} value={metrics.newCustomerRate} suffix="%" precision={1} />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {campaignUtms.length > 0 && (
        <Card title={t("utm_parameters")} style={{ marginTop: 16 }} loading={utmsLoading}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {campaignUtms.map((utm) => (
              <Card key={utm.id} type="inner" title={utm.name}>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label={t("utm_source")}>
                    <Tag>{utm.source}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("utm_medium")}>
                    <Tag>{utm.medium}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("utm_campaign")}>
                    <Tag>{utm.campaign}</Tag>
                  </Descriptions.Item>
                  {utm.term && (
                    <Descriptions.Item label={t("utm_term")}>
                      <Tag>{utm.term}</Tag>
                    </Descriptions.Item>
                  )}
                  {utm.content && (
                    <Descriptions.Item label={t("utm_content")}>
                      <Tag>{utm.content}</Tag>
                    </Descriptions.Item>
                  )}
                  {utm.customUrl && (
                    <Descriptions.Item label={t("custom_page_url")} span={2}>
                      <Text code>{utm.customUrl}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Divider style={{ margin: "12px 0" }} />

                <Row align="middle" gutter={[8, 8]}>
                  <Col flex="auto">
                    <Text strong>{t("generated_utm_url")}:</Text>
                    <br />
                    <Text code copyable style={{ wordBreak: "break-all" }}>
                      {generateUTMUrl(utm)}
                    </Text>
                  </Col>
                  <Col>
                    <Button icon={<CopyOutlined />} onClick={() => copyUTMUrl(utm)}>
                      {t("copy_url")}
                    </Button>
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
        </Card>
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: 16 }}>
        <Tabs.TabPane
          tab={
            <span>
              <LineChartOutlined /> {t("performance")}
            </span>
          }
          key="performance"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card title={t("revenue_over_time")} loading={metricsLoading}>
                {timeSeriesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Line type="monotone" dataKey="revenue" stroke="#8884d8" name={t("revenue")} />
                      <Line type="monotone" dataKey="orders" stroke="#82ca9d" name={t("orders")} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <Text type="secondary">{t("no_data_available")}</Text>
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={24}>
              <Card title={t("attribution_methods")} loading={metricsLoading}>
                {attributionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={attributionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="method" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill="#8884d8" name={t("orders")} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <Text type="secondary">{t("no_data_available")}</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              <ShoppingOutlined /> {t("products")}
            </span>
          }
          key="products"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card title={t("top_products")} loading={metricsLoading}>
                <Table
                  dataSource={productAnalysis.topProducts}
                  columns={productColumns}
                  rowKey="productId"
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </Col>
            <Col xs={24}>
              <Card title={t("cross_sell_analysis")} loading={metricsLoading}>
                <Table
                  dataSource={crossSellAnalysis.topPairs}
                  columns={crossSellColumns}
                  rowKey={(record) => `${record.product1.id}_${record.product2.id}`}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </Col>
          </Row>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default CampaignDetail;
